import { ChevronBottomOutlineIcon, ChevronTopOutlineIcon } from '@ikyucom/design-system-icons-react'
import { NoImage, SelectBox } from 'macaron-frontend-ui'
import React, { useCallback } from 'react'
import Image from '~/components/image/Image'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import { useChangeCount } from '~/features/reservationInput/childMenu/useChangeCount'
import { useMenuCard } from '~/features/reservationInput/childMenu/useMenuCard'
import type { ReserveChildMenuInput } from '~/lib/graphql/graphql'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { useReserveNavigate, useReserveOverlay, useSafeBack } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { Button } from '~/ui/Button'
import { useSetChildMenu } from '../store/childMenu'
import { disabledChildMenu } from '../store/stateMachine/assign'
import { usePlanId, useVisitDate, useVisitorsCount } from '../store/stateMachine/context'
import type { ReserveInputChildMenu } from '../store/stateMachine/state'

export function ChildMenusFusuma() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_CHILD_COUNT')
  if (context === undefined) {
    return null
  }
  return (
    <StackableFusuma title="お子様の利用" willBeUnmounted={willBeUnmounted}>
      <ChangeCount />
    </StackableFusuma>
  )
}

export function ChildMenusModal() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_CHILD_COUNT')
  if (context === undefined) {
    return null
  }
  return (
    <StackableSlideUpModal willBeUnmounted={willBeUnmounted}>
      <ChangeCount />
    </StackableSlideUpModal>
  )
}

const changeCountStyles = sva({
  slots: [
    'container',
    'title',
    'content',
    'change',
    'subtitle',
    'total',
    'breakdown',
    'item',
    'note',
  ],
  base: {
    container: {
      textAlign: 'left',
      pb: '12',
      _pc: {
        spaceY: '5',
        pb: '0',
      },
    },
    title: {
      _pc: {
        textAlign: 'left',
        fontSize: '2xl',
        fontWeight: 'bold',
        px: '6',
        pt: '6',
      },
    },
    content: {
      mx: '3',
      spaceY: '4',
      _pc: {
        mx: '0',
        px: '6',
        pb: '12',
        spaceY: '5',
      },
    },
    change: {
      spaceY: '4',
      _pc: {
        spaceY: '3',
      },
    },
    subtitle: {
      mt: '4',
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'xl',
        mt: '0',
      },
    },
    total: {
      borderRadius: 'md',
      border: 'medium',
      p: '4',
    },
    breakdown: {
      display: 'flex',
      spaceX: '4',
    },
    item: {
      spaceY: '1',
      _pc: {
        display: 'flex',
        alignItems: 'center',
        spaceY: '0',
        spaceX: '2',
        fontSize: 'md',
      },
    },
    note: {
      fontSize: 'xs',
      color: 'gray.600',
      _pc: {
        fontSize: 'sm',
      },
    },
  },
})

function ChangeCount() {
  const {
    adultCount,
    childCount,
    selectedChildMenus,
    childMenus,
    changeAdults,
    changeChildren,
    selectChildMenu,
    adultOptions,
    childOptions,
  } = useChangeCount()
  const isPc = useIsPC()
  const Submit = isPc ? PcSubmit : SdSubmit
  const MenuCard = isPc ? PcMenuCard : SdMenuCard
  const classes = changeCountStyles()
  return (
    <div className={classes.container}>
      {isPc && <h2 className={classes.title}>お子様のご利用</h2>}
      <div className={classes.content}>
        <div className={classes.change}>
          <p className={classes.subtitle}>人数内訳変更</p>
          <div className={classes.total}>
            <TotalVisitorsCount />
            <div className={classes.breakdown}>
              <div className={classes.item}>
                <p>大人</p>
                <SelectBox
                  value={`${adultCount}名`}
                  values={adultOptions}
                  width={150}
                  onChange={changeAdults}
                />
              </div>
              <div className={classes.item}>
                <p>子供</p>
                <SelectBox
                  value={`${childCount}名`}
                  values={childOptions}
                  width={150}
                  onChange={changeChildren}
                />
              </div>
            </div>
          </div>
          <p className={classes.note}>
            ※「子供」には大人と同じプランを利用しないお子様の人数をご入力ください。お子様は席のみの確保となり、プランの注文数には含まれません。
          </p>
        </div>
        <SelectChildMenus childCount={childCount}>
          {childMenus.map((menu) => (
            <MenuCard
              menu={menu}
              key={menu.childMenuId}
              selected={selectedChildMenus}
              selectChildMenu={selectChildMenu}
              childCount={childCount}
            />
          ))}
        </SelectChildMenus>
      </div>
      <Submit selected={selectedChildMenus} childCount={childCount} />
    </div>
  )
}

const totalVisitorsCountStyles = sva({
  slots: ['container', 'title'],
  base: {
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '3',
    },
    title: {
      fontSize: 'base',
      _pc: {
        fontSize: 'md',
      },
    },
  },
})

function TotalVisitorsCount() {
  const { onClick, visitorsCount } = useTotalVisitorsCount()
  const classes = totalVisitorsCountStyles()
  return (
    <div className={classes.container}>
      <p className={classes.title}>
        総人数
        <span className={css({ fontWeight: 'bold', fontSize: 'lg', _pc: { fontSize: 'xl' } })}>
          {visitorsCount}名
        </span>
        の内訳
      </p>
      <Button color="tertiary" size="xs" onClick={onClick}>
        総人数を変更
      </Button>
    </div>
  )
}

function useTotalVisitorsCount() {
  const navigate = useReserveNavigate()
  const onClick = useCallback(() => {
    navigate({ type: 'changeTotalCount' })
  }, [navigate])
  const planId = usePlanId()
  const visitorsCount = useVisitorsCount().toString()
  const visitDate = useVisitDate()
  return { onClick, planId, visitorsCount, visitDate }
}

const selectChildMenusStyles = sva({
  slots: ['container', 'heading', 'title', 'note'],
  base: {
    container: {
      spaceY: '3',
      pb: '[80px]',
      _pc: {
        spaceY: '3',
        pb: '0',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'xl',
      },
    },
    note: {
      fontSize: 'xs',
      color: 'gray.600',
      _pc: {
        fontSize: 'sm',
      },
    },
  },
})

function SelectChildMenus({
  children,
  childCount,
}: React.PropsWithChildren<{ childCount: number }>) {
  // お子様メニュー(children)がないプランの場合、または子供の人数が0名の場合はお子様メニューを表示しない
  if (React.Children.count(children) === 0 || childCount === 0) {
    return null
  }
  const classes = selectChildMenusStyles()
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <p className={classes.title}>お子様メニュー</p>
        <div className={classes.note}>※写真はイメージです</div>
      </div>
      {children}
    </div>
  )
}

const SdMenuCardStyles = sva({
  slots: [
    'container',
    'image',
    'content',
    'name',
    'detail',
    'button',
    'select',
    'price',
    'selectbox',
    'description',
    'heading',
  ],
  base: {
    container: {
      borderRadius: 'md',
      border: 'medium',
      borderColor: 'gray.300',
      p: '4',
    },
    image: {
      display: 'flex',
      alignItems: 'start',
    },
    content: {
      display: 'grid',
      gap: '2',
      alignContent: 'space-between',
      marginLeft: '2',
      width: 'full',
    },
    name: {
      width: '[176px]',
      fontWeight: 'bold',
    },
    detail: {
      display: 'flex',
      width: 'full',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '1',
      fontSize: 'sm',
      color: 'link',
      flexShrink: '0',
    },
    select: {
      marginLeft: '3',
      display: 'flex',
      width: 'full',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    price: {
      fontSize: 'lg',
      fontWeight: 'bold',
    },
    selectbox: {
      marginLeft: '3',
    },
    description: {
      marginTop: '2',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      bg: 'gray.100',
      p: '3',
      fontSize: 'sm',
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
})

function SdMenuCard({
  menu,
  childCount,
  selected,
  selectChildMenu,
}: {
  menu: ReserveInputChildMenu
  childCount: number
  selected: ReserveChildMenuInput[]
  selectChildMenu: (
    e: React.ChangeEvent<HTMLSelectElement>,
    childMenu: ReserveInputChildMenu
  ) => void
}) {
  const { opened, toggle, options, value } = useMenuCard(menu, childCount, selected)
  const classes = SdMenuCardStyles()
  return (
    <div className={classes.container}>
      <div className={classes.image}>
        {menu.image !== null ? (
          <Image
            alt={menu.image.name}
            src={menu.image.url}
            width={70}
            height={70}
            className={css({ rounded: 'default' })}
          />
        ) : (
          <NoImage width={70} height={70} />
        )}
        <div className={classes.content}>
          <p className={classes.name}>{menu.name}</p>
          <div className={classes.detail}>
            {menu.description !== '' && (
              <button type="button" className={classes.button} onClick={toggle}>
                {opened ? '閉じる' : '詳細をみる'}
              </button>
            )}
            <div className={classes.select}>
              <p className={classes.price}>{money(menu.price)}円</p>
              <div className={classes.selectbox}>
                <SelectBox
                  values={options}
                  value={value}
                  width={78}
                  onChange={(e) => selectChildMenu(e, menu)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {opened && <p className={classes.description}>{menu.description}</p>}
    </div>
  )
}

const PcMenuCardStyles = sva({
  slots: [
    'container',
    'image',
    'content',
    'heading',
    'name',
    'select',
    'price',
    'selectbox',
    'button',
    'description',
  ],
  base: {
    container: {
      borderRadius: 'md',
      border: 'medium',
      borderColor: 'gray.300',
      p: '4',
    },
    image: {
      display: 'flex',
      alignItems: 'start',
    },
    content: {
      alignContent: 'space-between',
      marginLeft: '2',
      width: 'full',
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    name: {
      fontWeight: 'bold',
      flex: '1',
    },
    select: {
      marginLeft: '3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    price: {
      fontSize: 'lg',
      fontWeight: 'bold',
    },
    selectbox: {
      marginLeft: '3',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '1',
      color: 'link',
      fontSize: 'sm',
    },
    description: {
      marginTop: '2',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: 'sm',
      bg: 'gray.100',
      p: '3',
      rounded: 'sm',
    },
  },
})

function PcMenuCard({
  menu,
  childCount,
  selected,
  selectChildMenu,
}: {
  menu: ReserveInputChildMenu
  childCount: number
  selected: ReserveChildMenuInput[]
  selectChildMenu: (
    e: React.ChangeEvent<HTMLSelectElement>,
    childMenu: ReserveInputChildMenu
  ) => void
}) {
  const { opened, toggle, options, value } = useMenuCard(menu, childCount, selected)
  const classes = PcMenuCardStyles()
  return (
    <div className={classes.container}>
      <div className={classes.image}>
        {menu.image !== null ? (
          <Image
            alt={menu.image.name}
            src={menu.image.url}
            width={90}
            height={90}
            className={css({ rounded: 'default' })}
          />
        ) : (
          <NoImage width={90} height={90} />
        )}
        <div className={classes.content}>
          <div className={classes.heading}>
            <p className={classes.name}>{menu.name}</p>
            <div className={classes.select}>
              <p className={classes.price}>{money(menu.price)}円</p>
              <div className={classes.selectbox}>
                <SelectBox
                  values={options}
                  value={value}
                  width={78}
                  onChange={(e) => selectChildMenu(e, menu)}
                />
              </div>
            </div>
          </div>
          {menu.description !== '' && (
            <button type="button" className={classes.button} onClick={toggle}>
              {opened ? (
                <>
                  <p>閉じる</p>
                  <ChevronTopOutlineIcon />
                </>
              ) : (
                <>
                  <p>詳細をみる</p>
                  <ChevronBottomOutlineIcon />
                </>
              )}
            </button>
          )}
          {menu.description !== '' && opened && (
            <p className={classes.description}>{menu.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

type SubmitProps = {
  childCount: number
  selected: ReserveChildMenuInput[]
}

const sdSubmitStyles = sva({
  slots: ['container', 'error', 'text'],
  base: {
    container: {
      position: 'fixed',
      bottom: '0',
      width: 'full',
      bg: 'white',
      px: '3',
      pb: '3',
    },
    error: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    text: {
      pointerEvents: 'none',
      position: 'absolute',
      insetX: '0',
      bottom: '0',
      width: 'full',
      bg: 'white',
      py: '3',
      textAlign: 'left',
      fontSize: 'sm',
      color: 'error.text',
    },
  },
})

function SdSubmit({ childCount, selected }: SubmitProps) {
  const submit = useSetChildMenu()
  const disabled = disabledChildMenu(childCount, selected)
  const classes = sdSubmitStyles()
  return (
    <div className={classes.container}>
      {disabled && (
        <div className={classes.error}>
          <p className={classes.text}>
            お子様用メニューの数がお子様の人数を超えています。お子様用メニューもしくはお子様の人数を変更してください。
          </p>
        </div>
      )}
      <Button
        color="secondary"
        size="md"
        disabled={disabled}
        onClick={() => submit(childCount, selected)}
        spread={true}
      >
        決定する
      </Button>
    </div>
  )
}

const pcSubmitStyles = sva({
  slots: ['container', 'error', 'text', 'button', 'cancel'],
  base: {
    container: {
      position: 'sticky',
      left: '0',
      bottom: '0',
      bg: 'white',
      shadow: 'outline.lg',
      px: '6',
      pt: '4',
      pb: '8',
    },
    text: {
      pointerEvents: 'none',
      width: 'full',
      bg: 'white',
      pb: '2',
      textAlign: 'left',
      fontSize: 'sm',
      color: 'error.text',
    },
    button: {
      spaceY: '5',
      spaceX: '4',
      px: '6',
      pt: '4',
      pb: '10',
    },
    cancel: {
      color: 'gray.700',
      py: '3',
      px: '2',
      rounded: 'md',
      transition: '[all 0.2s]',
      _hover: {
        bg: 'gray.200',
      },
    },
  },
})

function PcSubmit({ childCount, selected }: SubmitProps) {
  const submit = useSetChildMenu()
  const disabled = disabledChildMenu(childCount, selected)
  const back = useSafeBack()
  const classes = pcSubmitStyles()
  const onSubmit = useCallback(() => {
    if (!disabled) {
      submit(childCount, selected)
    }
  }, [disabled, submit, childCount, selected])
  return (
    <div className={classes.container}>
      {disabled && (
        <p className={classes.text}>
          お子様用メニューの数がお子様の人数を超えています。お子様用メニューもしくはお子様の人数を変更してください。
        </p>
      )}
      <div className={hstack({ justify: 'flex-end', gap: '4' })}>
        <button type="button" onClick={back} className={classes.cancel}>
          キャンセル
        </button>
        <Button color="secondary" size="lg" asChild disabled={disabled}>
          <button type="button" onClick={onSubmit}>
            決定する
          </button>
        </Button>
      </div>
    </div>
  )
}
